import { ChangeEvent, Dispatch, SetStateAction, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { Box, Button, Dialog, FormControl, TextField } from "@mui/material"
import { Check } from '@mui/icons-material'
import CloseModalButton from "../../../../components/CloseModalButton"
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../../components/Dialog.styled"
import { getAutoclaves, getSporetests, updateSporeTest } from "../../../../services/biologicalServices"
import {  Autoclave, SporetestingResult } from "../types"
import { addError } from "../../../../redux/alerts"
import { useAppDispatch } from "../../../../redux/store"

type AddressSporeTestModal = {
  customerNumber: string,
  open: boolean,
  sporetest: SporetestingResult,
  handleClose: () => void,
  setAutoclaves: Dispatch<SetStateAction<Autoclave[]>>,
  setSporetests: Dispatch<SetStateAction<SporetestingResult[]>>,
}

const AddressSporeTestModal = (props: AddressSporeTestModal) => {
  const {
    customerNumber,
    open,
    sporetest,
    handleClose,
    setAutoclaves,
    setSporetests,
  } = props

  const dispatch = useAppDispatch();
  const validationInitialState = {
    correctiveAction: { isValid: true, message: '' },
  }

  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [addressedOn, setAddressedOn] = useState<Dayjs | null>(dayjs())
  const [correctiveAction, setCorrectiveAction] = useState<string | null>(sporetest.note) 
  const [validations, setValidations] = useState(validationInitialState)

  const handleSave = async () => {
    if (addressedOn && correctiveAction) {
      try {
        setIsUpdating(true)

        const cloned = structuredClone(sporetest)

        cloned.note = correctiveAction
        cloned.addressed_on = addressedOn.utc().hour(0).format('YYYY-MM-DDTHH:mm:ss')

        await updateSporeTest(customerNumber, cloned)

        const results = await Promise.allSettled([
          getAutoclaves(customerNumber),
          getSporetests(customerNumber),
        ])

        const [autoclaveResult, sporetestResult] = results; 

        if (autoclaveResult.status === 'fulfilled') {
          setAutoclaves(autoclaveResult.value);
        } else {
          console.error(autoclaveResult.reason);
          dispatch(addError('Failed to load autoclaves'));
        }

        if (sporetestResult.status === 'fulfilled') {
          setSporetests(sporetestResult.value);
        } else {
          console.error(sporetestResult.reason);
          dispatch(addError('Failed to load sporetests'));
        }

        setIsUpdating(false)
        handleClose()
      } catch (err) {
        console.error(err)
        setIsUpdating(false)
        dispatch(addError(err instanceof Error ? err.message : 'There was a problem addressing the Spore test'))
        handleClose()
      }
    }
  }

  const handleSelectedDate = (date: Dayjs | null) => {
    setAddressedOn(date)
  }

  const handleCorrectiveAction = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const note = event.target.value

    const isValidNote = Boolean(note) && note.length <= 255
    
    const errorMessage = Boolean(note) && note.length > 255
      ? 'Corrective Action Too Long'
      : !Boolean(note) ? 'Corrective Action Required' : ''

    const cloned = structuredClone(validations)
    cloned.correctiveAction = { isValid: isValidNote, message: errorMessage}

    setValidations(cloned)
    setCorrectiveAction(note)
  }

  const disableSave = isUpdating || !addressedOn || !correctiveAction || !validations.correctiveAction.isValid

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <CloseModalButton handleClose={handleClose} />
        <StyledDialogTitle>
          <Box className="flex flex-align-center" columnGap='4px'>
            <Check sx={{ color: '#4ddb6f', verticalAlign: 'middle', marginRight: '2px' }}/>
            Address Spore Test
          </Box>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Box className="m-t-16 m-b-16">
            <FormControl size="small" fullWidth>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                <DatePicker
                  label="Addressed On (required)"
                  minDate={dayjs(sporetest.test_date)}
                  maxDate={dayjs()}
                  slotProps={{ textField: { size: 'small', error: !Boolean(addressedOn) } }}
                  value={addressedOn}
                  onChange={(selectedDate) => handleSelectedDate(selectedDate)}
                  format="MM-DD-YYYY"
                />
              </LocalizationProvider>
            </FormControl>            
          </Box>
          <Box>
            <TextField
              className="m-b-16"
              fullWidth
              label="Corrective Action (required)"
              value={correctiveAction ?? ''}
              onChange={handleCorrectiveAction}
              error={!validations.correctiveAction.isValid}
              helperText={validations.correctiveAction.message}
              InputProps={{
                rows: 10,
                multiline: true,
                inputComponent: 'textarea'
              }}
            />
          </Box>
        </StyledDialogContent>
        <StyledDialogFooter>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={disableSave}>
            OK
          </Button>
        </StyledDialogFooter>
      </Dialog>
    </>
  )
}

export default AddressSporeTestModal
